import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, Sparkles, ChevronLeft, ChevronRight, ArrowDown, Plus, Clock, MapPin, User, Ticket, Globe, Info, Calendar as CalendarIcon } from 'lucide-react';
import { CATEGORIES, BUSINESSES } from '../constants';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import Newsletter from '../components/Newsletter';
import SEO from '../components/SEO';

export default function Home() {
  const navigate = useNavigate();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [events, setEvents] = useState<any[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const homeJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://thelocalagora.com/#website",
        "url": "https://thelocalagora.com/",
        "name": "The Local Agora",
        "description": "Discover local events, small businesses, artists, community experiences, and hidden gems across Windsor-Essex through The Local Agora.",
        "publisher": {
          "@id": "https://thelocalagora.com/#organization"
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://thelocalagora.com/partners?search={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "Organization",
        "@id": "https://thelocalagora.com/#organization",
        "name": "The Local Agora",
        "url": "https://thelocalagora.com/",
        "logo": {
          "@type": "ImageObject",
          "url": "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?fm=webp&fit=crop&q=80&w=600"
        },
        "sameAs": [
          "https://instagram.com",
          "https://facebook.com"
        ]
      },
      {
        "@type": "WebPage",
        "@id": "https://thelocalagora.com/#webpage",
        "url": "https://thelocalagora.com/",
        "name": "The Local Agora | Windsor’s Local Discovery Marketplace",
        "isPartOf": {
          "@id": "https://thelocalagora.com/#website"
        },
        "about": {
          "@id": "https://thelocalagora.com/#organization"
        },
        "description": "Discover local events, small businesses, artists, community experiences, and hidden gems across Windsor-Essex through The Local Agora."
      }
    ]
  };

  const [config, setConfig] = useState<any>(null);

  useEffect(() => {
    const configDocRef = doc(db, 'connections_config', 'settings');
    const unsubConfig = onSnapshot(configDocRef, (snapshot) => {
      if (snapshot.exists()) {
        setConfig(snapshot.data());
      } else {
        setConfig({
          featuredLive: false,
          featuredSlugs: []
        });
      }
    }, (error) => {
      console.error("Error loading connections config:", error);
    });
    return () => unsubConfig();
  }, []);

  const featuredBusinesses = BUSINESSES.filter(biz => {
    const activeSlugs = config?.activeSlugs ?? BUSINESSES.map(b => b.slug);
    if (!activeSlugs.includes(biz.slug)) return false;

    if (config?.featuredSlugs) {
      return config.featuredSlugs.includes(biz.slug);
    }
    return biz.featured === true;
  });

  useEffect(() => {
    if (currentIdx >= featuredBusinesses.length) {
      setCurrentIdx(0);
    }
  }, [featuredBusinesses.length, currentIdx]);

  useEffect(() => {
    if (featuredBusinesses.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIdx((prev) => (prev + 1) % featuredBusinesses.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featuredBusinesses.length]);

  const getLocalDateString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    async function fetchLatestEvents() {
      try {
        const q = query(
          collection(db, 'events'),
          where('status', '==', 'approved'),
          orderBy('eventDate', 'asc')
        );
        const querySnapshot = await getDocs(q);
        const list: any[] = [];
        querySnapshot.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() });
        });
        
        // Filter out past events safely using local timezone string & exclude Series Masters
        const todayStr = getLocalDateString();
        const upcoming = list.filter(evt => evt.eventDate >= todayStr && evt.isSeriesMaster !== true);
        
        // Show up to the next 3 or 4 upcoming events on the homepage
        setEvents(upcoming.slice(0, 3));
      } catch (err) {
        console.error("Error fetching homepage events: ", err);
      } finally {
        setEventsLoading(false);
      }
    }
    fetchLatestEvents();
  }, []);

  const next = () => {
    if (featuredBusinesses.length > 0) {
      setCurrentIdx((prev) => (prev + 1) % featuredBusinesses.length);
    }
  };
  const prev = () => {
    if (featuredBusinesses.length > 0) {
      setCurrentIdx((prev) => (prev - 1 + featuredBusinesses.length) % featuredBusinesses.length);
    }
  };

  const featuredBiz = featuredBusinesses[currentIdx] || featuredBusinesses[0];

  const formatPillDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr + "T00:00:00");
      if (isNaN(d.getTime())) {
        return { day: '15', month: 'EVT' };
      }
      const day = String(d.getDate()).padStart(2, '0');
      const month = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
      return { day, month };
    } catch (err) {
      return { day: '15', month: 'EVT' };
    }
  };

  const displayEvents = events;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="max-w-7xl mx-auto px-6"
    >
      <SEO 
        title="The Local Agora | Windsor’s Local Discovery Marketplace" 
        description="Discover local events, small businesses, artists, community experiences, and hidden gems across Windsor-Essex through The Local Agora." 
        path="/"
        jsonLd={homeJsonLd}
      />
      {/* Hero Section */}
      <section className="py-20 flex flex-col lg:flex-row items-center gap-16">
        <div className="flex-1 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-agora-tan/30 rounded-full text-agora-olive text-xs font-semibold uppercase tracking-widest mb-6">
              <Sparkles size={12} />
              <span>Connecting Windsor-Essex</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-serif font-bold leading-[0.9] tracking-tight mb-8">
              Discover the <br />
              <span className="text-agora-rust italic">Extraordinary</span> <br />
              Around You
            </h1>
            <p className="text-xl text-agora-ink/70 max-w-lg leading-relaxed font-sans">
              The Local Agora is a digital marketplace where Windsor-Essex's best small businesses, instructors, and events get discovered.
            </p>
          </motion.div>

          <div className="flex items-center gap-6 pt-4">
            <Link to="/join" className="h-14 px-8 bg-agora-rust text-white rounded-full flex items-center gap-2 font-medium hover:scale-105 transition-transform">
              List Your Business <ArrowRight size={18} />
            </Link>
            <a href="#explore" className="h-14 px-8 bg-agora-ink text-white rounded-full flex items-center gap-2 font-medium hover:scale-105 transition-transform">
              Start Exploring <ArrowDown size={18} />
            </a>
          </div>
        </div>

        <div className="flex-1 w-full relative">
          <motion.div
            initial={{ scale: 0.9, opacity: 0, rotate: -3 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ duration: 0.8 }}
            className="aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl relative z-10 font-sans"
          >
            <img 
              src="https://images.unsplash.com/photo-1517457373958-b7bdd4587205?fm=webp&fit=crop&q=80&w=1200" 
              srcSet="https://images.unsplash.com/photo-1517457373958-b7bdd4587205?fm=webp&fit=crop&q=80&w=600 600w, https://images.unsplash.com/photo-1517457373958-b7bdd4587205?fm=webp&fit=crop&q=80&w=1200 1200w"
              sizes="(max-width: 768px) 100vw, 50vw"
              alt="Vibrant outdoor local community market in Windsor with vendors and visitors"
              className="w-full h-full object-cover"
              fetchPriority="high"
              width={600}
              height={750}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-agora-ink/60 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-white">
              <p className="font-serif text-2xl italic tracking-tight mb-1">"The best things aren't always easy to find—unless you're here."</p>
              <p className="text-xs uppercase tracking-widest opacity-70">— Agora Community</p>
            </div>
          </motion.div>
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-agora-tan rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-agora-rust rounded-full blur-[80px] opacity-20" />
        </div>
      </section>

      {/* Featured Section - Rotating Banner */}
      {config?.featuredLive && featuredBusinesses.length > 0 && (
        <section id="explore" className="py-24 border-t border-agora-ink/5">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="space-y-4">
              <h2 className="text-4xl font-serif font-bold tracking-tight">Featured this week</h2>
              <p className="text-agora-ink/60 font-sans max-w-md">Discover the local masters crafting unique experiences right now.</p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/partners" className="text-sm font-semibold uppercase tracking-widest flex items-center gap-2 hover:text-agora-rust transition-colors mr-4">
                View All Partners <ArrowRight size={16} />
              </Link>
              {featuredBusinesses.length > 1 && (
                <div className="flex gap-2">
                  <button 
                    onClick={prev}
                    className="w-12 h-12 rounded-full border border-agora-ink/10 flex items-center justify-center hover:bg-agora-ink hover:text-white transition-all"
                    aria-label="Previous Featured Business"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button 
                    onClick={next}
                    className="w-12 h-12 rounded-full border border-agora-ink/10 flex items-center justify-center hover:bg-agora-ink hover:text-white transition-all"
                    aria-label="Next Featured Business"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="relative overflow-hidden min-h-[500px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={featuredBiz.id}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="w-full"
              >
                {featuredBiz.externalUrl ? (
                  <a href={featuredBiz.externalUrl} target="_blank" rel="noopener noreferrer" className="block group">
                    <FeaturedCard biz={featuredBiz} />
                  </a>
                ) : (
                  <Link to={`/${featuredBiz.slug}`} className="block group">
                    <FeaturedCard biz={featuredBiz} />
                  </Link>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </section>
      )}

      {/* What's Happening in Windsor-Essex Bulletin Board */}
      <section className="py-24 border-t border-agora-ink/5 bg-gradient-to-b from-transparent to-agora-tan/10">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Header & Balance Button CTA Row */}
          <div className="mb-16 space-y-6">
            <div className="space-y-4 max-w-5xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-agora-rust/10 rounded-full text-agora-rust text-xs font-semibold uppercase tracking-widest">
                <Sparkles size={12} />
                <span>CURATED LOCAL EVENTS</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-agora-ink leading-tight">
                What’s Happening in Windsor–Essex
              </h2>
            </div>
            
            {/* CTA balanced buttons - placed on their own line */}
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-max">
              <Link
                to="/events"
                className="h-12 px-8 bg-agora-rust text-white rounded-full inline-flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-widest hover:bg-agora-ink hover:scale-[1.02] transition-all duration-300 shadow-sm text-center"
              >
                Explore Events
                <ArrowRight size={14} />
              </Link>
              <Link
                to="/events?submit=true"
                className="h-12 px-8 bg-agora-ink text-white rounded-full inline-flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-widest hover:bg-agora-rust hover:scale-[1.02] transition-all duration-300 shadow-sm text-center"
              >
                Submit Event
                <Plus size={14} />
              </Link>
            </div>
          </div>

          {/* Main Events Cards Focus */}
          {eventsLoading ? (
            <div className="flex flex-col gap-4 min-h-[350px] justify-center items-center bg-white rounded-3xl border border-agora-ink/5 shadow-xs">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-10 h-10 border-3 border-agora-rust border-t-transparent rounded-full"
              />
              <p className="text-xs text-agora-ink/40 font-mono tracking-widest uppercase">Syncing community board...</p>
            </div>
          ) : displayEvents.length === 0 ? (
            <div className="text-center py-16 px-6 bg-white rounded-[2rem] border border-agora-ink/5 shadow-xs space-y-4 max-w-2xl mx-auto">
              <p className="text-agora-ink/65 font-sans leading-relaxed text-sm md:text-base">
                No upcoming events are listed yet. Check back soon or submit your local event.
              </p>
              <Link
                to="/events?submit=true"
                className="h-11 px-6 bg-agora-rust text-white rounded-full inline-flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-widest hover:bg-agora-ink transition-all"
              >
                Submit Your Event
                <Plus size={14} />
              </Link>
            </div>
          ) : (
            <div className="space-y-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {displayEvents.map((evt, idx) => {
                  return (
                    <motion.div
                      key={evt.id || idx}
                      className="bg-white rounded-3xl border border-agora-ink/5 hover:border-agora-rust/35 p-6 md:p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-all h-full group relative"
                      whileHover={{ y: -3 }}
                    >
                      <div className="space-y-4">
                        <div className="flex justify-between items-start gap-4">
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-agora-tan/20 text-agora-ink/75 px-2.5 py-1 rounded-md">
                            {evt.category || 'Other'}
                          </span>
                          <div className="text-[10px] uppercase font-bold text-agora-rust/70 tracking-widest flex items-center gap-1.5 bg-agora-rust/5 px-2.5 py-1 rounded-md">
                            <CalendarIcon size={11} /> {new Date(evt.eventDate + "T00:00:00").toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                        </div>
                        
                        <h4 
                          onClick={() => navigate('/events', { state: { selectedEventId: evt.id } })}
                          className="text-xl font-bold text-agora-ink leading-tight group-hover:text-agora-rust transition-colors font-serif cursor-pointer"
                        >
                          {evt.eventName}
                        </h4>
                        
                        {/* Tappable description block */}
                        <p 
                          onClick={() => navigate('/events', { state: { selectedEventId: evt.id } })}
                          className="text-sm text-agora-ink/65 leading-relaxed font-sans line-clamp-4 whitespace-pre-line cursor-pointer hover:text-agora-rust/95 transition-colors focus:outline-none focus:ring-1 focus:ring-agora-rust rounded-md"
                          title="Click to view full description and details"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate('/events', { state: { selectedEventId: evt.id } });
                            }
                          }}
                        >
                          {evt.description}
                        </p>
                      </div>
                      
                      <div className="pt-6 border-t border-agora-ink/5 mt-6 space-y-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2.5 text-xs text-agora-ink">
                            <Clock size={14} className="text-agora-rust opacity-60 flex-shrink-0" />
                            <span className="font-semibold truncate">{evt.eventTime}</span>
                          </div>
                          <div className="flex items-center gap-2.5 text-xs text-agora-ink/60">
                            <MapPin size={14} className="text-agora-rust opacity-40 flex-shrink-0" />
                            <span className="font-medium truncate">{evt.location}</span>
                          </div>
                          <div className="flex items-center gap-2.5 text-xs text-agora-ink/60">
                            <User size={14} className="text-agora-rust opacity-40 flex-shrink-0" />
                            <span className="font-medium truncate">Organizer / Source: {evt.hostName || 'Local Guest'}</span>
                          </div>
                        </div>

                        {/* Event link Actions */}
                        <div className="flex flex-wrap gap-2 pt-2">
                          <button
                            onClick={() => navigate('/events', { state: { selectedEventId: evt.id } })}
                            className="h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-widest bg-agora-cream text-agora-ink border border-agora-ink/5 hover:bg-agora-tan/20 hover:text-agora-ink transition-colors flex items-center gap-1.5 cursor-pointer"
                            title="View Event Details on Events Page"
                          >
                            <Info size={13} /> Details
                          </button>

                          {evt.ticketLink && (
                            <a
                              href={evt.ticketLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              referrerPolicy="no-referrer"
                              className="h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-widest bg-agora-rust text-white hover:bg-agora-ink transition-colors flex items-center gap-1.5"
                            >
                              <Ticket size={13} /> Tickets
                            </a>
                          )}
                          
                          {evt.websiteLink && (
                            <a
                              href={evt.websiteLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              referrerPolicy="no-referrer"
                              className="h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-widest bg-white text-agora-ink border border-agora-ink/15 hover:bg-agora-cream transition-colors flex items-center gap-1.5"
                            >
                              <Globe size={13} /> Website
                            </a>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Subdued footer notice */}
              <div className="text-center pt-4">
                <p className="text-xs text-agora-ink/40 font-medium font-sans">
                  New events are added regularly. Check back often.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Category Pillars */}
      <section className="py-24 bg-agora-ink text-agora-cream rounded-[3rem] px-12 mb-24 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-agora-rust rounded-full blur-[120px] opacity-20 -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative z-10">
          <div className="max-w-2xl mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight mb-6">Explore the <br /> Agora Pillars.</h2>
            <p className="opacity-60 leading-relaxed font-sans text-lg">We've organized the community into key discovery paths.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
            {CATEGORIES.map((cat, idx) => (
              <motion.div 
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="space-y-4 group cursor-pointer"
              >
                <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center group-hover:bg-agora-rust transition-colors">
                  <span className="text-xs font-bold font-serif opacity-50 group-hover:opacity-100 italic transition-opacity">0{idx + 1}</span>
                </div>
                <h4 className="text-xl font-serif font-bold group-hover:text-agora-rust transition-colors">{cat.title}</h4>
                <p className="text-sm opacity-40 leading-relaxed group-hover:opacity-60 transition-opacity">{cat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Newsletter />
    </motion.div>
  );
}

function FeaturedCard({ biz }: { biz: any }) {
  // Use a webp version of the Unsplash url for better performance
  const webpImage = biz.image ? biz.image.replace('auto=format', 'fm=webp') : '';
  const webpSrcSet = biz.image ? `${biz.image.replace('auto=format', 'fm=webp').replace('w=800', 'w=400')} 400w, ${biz.image.replace('auto=format', 'fm=webp')} 800w` : undefined;

  return (
    <div className="flex flex-col lg:flex-row gap-12 items-center">
      <div className="flex-1 w-full aspect-video lg:aspect-square rounded-[3rem] overflow-hidden shadow-xl">
        <img 
          src={webpImage} 
          srcSet={webpSrcSet}
          sizes="(max-width: 768px) 100vw, 400px"
          alt={biz.name} 
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
          loading="lazy"
          width={600}
          height={600}
        />
      </div>
      <div className="flex-1 space-y-8">
        <div className="space-y-4">
          <div className="inline-block px-4 py-1.5 bg-agora-tan/30 text-agora-olive rounded-full text-xs font-bold tracking-widest uppercase mb-4">
            {biz.category}
          </div>
          <h3 className="text-5xl md:text-6xl font-serif font-bold tracking-tight leading-tight group-hover:text-agora-rust transition-colors flex items-center gap-4 flex-wrap">
            <span>{biz.name}</span>
            {biz.comingSoon && (
              <span className="inline-flex items-center bg-agora-rust/15 text-agora-rust text-xs font-bold tracking-widest px-3.5 py-1 rounded-full uppercase font-sans">
                Coming Soon
              </span>
            )}
          </h3>
          <p className="text-xl text-agora-ink/60 leading-relaxed font-sans">
            {biz.description}
          </p>
        </div>
        
        <div className="flex flex-wrap gap-3">
          {biz.tags.map(tag => (
            <span key={tag} className="text-xs uppercase font-bold tracking-[0.2em] text-agora-ink/40">#{tag}</span>
          ))}
        </div>

        <div className="pt-6">
          <div className="h-14 px-8 bg-agora-ink text-white rounded-full inline-flex items-center gap-3 font-medium hover:bg-agora-rust transition-colors">
            Learn More <ArrowRight size={18} />
          </div>
        </div>
      </div>
    </div>
  );
}
